import { useCallback, useEffect, useRef } from "react";
import { EditQuizPageProps } from "../editQuiz";
import { UseEditQuizStateReturn } from "./state/useEditQuizState";

type UseEditQuizInitProps = Pick<
	EditQuizPageProps,
	"enforcedProfilesOnly" | "url" | "proctoringAssessmentConfiguration" | "setProctoringAssessmentConfiguration"
> &
	Pick<UseEditQuizStateReturn, "live" | "profile" | "setting" | "student" | "theme">;

/**
 * Call init functions here.
 * Originally in editQuiz.ts
 */
export default function useEditQuizInit({
	enforcedProfilesOnly,
	url,
	proctoringAssessmentConfiguration,
	setProctoringAssessmentConfiguration,
	...props
}: UseEditQuizInitProps) {
	const {
		getAssessmentUuids,
		fetchInstituteDataByUuid,
		fetchCourseData,
		fetchStaticData,
		setLoading,
		fetchReferenceMaterialFiles,
	} = props.setting.action;
	const { fetchAndSetLiveSessions } = props.live.action;
	const { fetchAndFilterProfiles, fetchInstituteProfilePermissions } = props.profile.action;
	const { fetchAndSetExemptionInfo } = props.student.action;
	const { initStandaloneExemptionInfo } = props.student.action;
	const { handleFetchAndSetTheme } = props.theme.action;

	const getEditAssessmentInfo = useCallback(async () => {
		try {
			setLoading(true);

			const { verityInstituteSettings, setupGuide, isSetupGuide } = await fetchStaticData();
			const { assessmentUUids, verityCourseId } = await getAssessmentUuids();
			await fetchInstituteDataByUuid(assessmentUUids.instituteUuid);
			await fetchInstituteProfilePermissions();

			if (verityCourseId) {
				await fetchCourseData(verityCourseId);
			}

			const tempProfileMap = await fetchAndFilterProfiles(
				verityInstituteSettings,
				setupGuide.setupProfileId ?? undefined,
				isSetupGuide,
			);
			await fetchAndSetLiveSessions(assessmentUUids);
			await fetchAndSetExemptionInfo(tempProfileMap);
			await fetchReferenceMaterialFiles();
		} catch (error) {
			console.error(error);
		} finally {
			setLoading(false);
		}
	}, [
		setLoading,
		fetchStaticData,
		getAssessmentUuids,
		fetchInstituteDataByUuid,
		fetchInstituteProfilePermissions,
		fetchAndFilterProfiles,
		fetchAndSetLiveSessions,
		fetchAndSetExemptionInfo,
		fetchReferenceMaterialFiles,
		fetchCourseData,
	]);

	const getEditAssessmentInfoRef = useRef(getEditAssessmentInfo);
	getEditAssessmentInfoRef.current = getEditAssessmentInfo;

	useEffect(() => {
		void getEditAssessmentInfoRef.current();

		// Using ref since quizInfo changes on save which causes init function to rerun and reset state
	}, [enforcedProfilesOnly]);

	// Populates thirdPartyUrl on mount if a url is passed to the parent
	useEffect(() => {
		if (
			url &&
			proctoringAssessmentConfiguration &&
			proctoringAssessmentConfiguration.thirdPartyAssessmentOption &&
			!proctoringAssessmentConfiguration.thirdPartyAssessmentOption?.thirdPartyUrl
		) {
			setProctoringAssessmentConfiguration({
				...proctoringAssessmentConfiguration,
				thirdPartyAssessmentOption: {
					...proctoringAssessmentConfiguration.thirdPartyAssessmentOption,
					thirdPartyUrl: url,
				},
			});
		}
	}, [proctoringAssessmentConfiguration, setProctoringAssessmentConfiguration, url]);

	useEffect(() => {
		handleFetchAndSetTheme();
	}, [handleFetchAndSetTheme]);

	useEffect(() => {
		initStandaloneExemptionInfo();
	}, [initStandaloneExemptionInfo]);
}
